// Non-production API host override. Mirrors the mobile development-settings
// rules (subdomain + custom URL, pin source) and keeps the resolved host in
// `debug_api_base_url`, which `getBaseUrl` already reads. A web preview at
// `pr-{id}-web.oneteam-preview.io` auto-selects `pr-{id}-api`.

export const API_BASE_URL_OVERRIDE_KEY = 'debug_api_base_url';
export const API_OVERRIDE_MODE_KEY = 'debug_api_override_mode';
export const API_OVERRIDE_SOURCE_KEY = 'debug_api_override_source';
export const API_OVERRIDE_SUBDOMAIN_KEY = 'debug_api_override_subdomain';
export const API_ENV_DISPLAY_NAME_KEY = 'debug_api_env_display_name';
export const API_ENV_DISPLAY_URL_KEY = 'debug_api_env_display_url';

export const API_PREVIEW_DOMAIN = 'oneteam-preview.io';
export const DEFAULT_PREVIEW_SUBDOMAIN = 'api';
export const DEFAULT_FREEFORM_HINT = 'http://localhost:8000';
export const DEFAULT_ENVIRONMENT_NAME = 'Default (build)';
export const AUTO_SELECT_TIMEOUT_MS = 3000;

const PREVIEW_LABEL = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i;
const WEB_PREVIEW_HOST = /^pr-(\d+)-web\.oneteam-preview\.io$/i;
const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

export type ApiOverrideMode = 'preview_subdomain' | 'freeform_url';
export type ApiOverrideSource = 'auto' | 'manual';

export type SubdomainValidationError =
  | 'empty'
  | 'scheme_or_path'
  | 'contains_dot'
  | 'invalid_characters';

export type FreeformValidationError =
  | 'empty'
  | 'invalid_url'
  | 'unsupported_scheme'
  | 'missing_host'
  | 'host_not_allowed';

export type HostVerificationError = 'unreachable' | 'unhealthy';
export type HealthResult = 'ok' | HostVerificationError;

export type CatalogLoadError = 'no_internet' | 'unauthorized' | 'http' | 'unknown';

export interface CatalogErrorState {
  error: CatalogLoadError;
  httpCode?: number;
  serverMessage?: string;
}

export interface PreviewEnvironment {
  label: string;
  url: string;
  kind: string;
  subdomain: string;
  pullRequestId?: number;
  commit?: string;
  commitMessage?: string;
  live?: boolean;
}

export interface StoredOverride {
  mode: ApiOverrideMode | null;
  source: ApiOverrideSource | null;
  subdomain: string | null;
  baseUrl: string | null;
}

export interface OverrideStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ValidSubdomain {
  valid: true;
  subdomain: string;
}

export interface InvalidSubdomain {
  valid: false;
  error: SubdomainValidationError;
}

export interface ValidFreeformUrl {
  valid: true;
  url: string;
}

export interface InvalidFreeformUrl {
  valid: false;
  error: FreeformValidationError;
}

interface RunAutoSelectOptions {
  hostname: string;
  verify: (url: string, signal: AbortSignal) => Promise<HealthResult>;
  read?: () => StoredOverride;
  write?: (subdomain: string) => void;
  timeoutMs?: number;
  signal?: AbortSignal;
}

const EMPTY_OVERRIDE: StoredOverride = {
  mode: null,
  source: null,
  subdomain: null,
  baseUrl: null,
};

function guardStore(store: OverrideStore): OverrideStore {
  return {
    getItem(key) {
      try {
        return store.getItem(key);
      } catch (error) {
        console.warn('API override storage read failed', error);
        return null;
      }
    },
    setItem(key, value) {
      try {
        store.setItem(key, value);
      } catch (error) {
        console.warn('API override storage write failed', error);
      }
    },
    removeItem(key) {
      try {
        store.removeItem(key);
      } catch (error) {
        console.warn('API override storage remove failed', error);
      }
    },
  };
}

function browserStore(): OverrideStore | null {
  try {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return null;
    return guardStore(window.localStorage);
  } catch (error) {
    console.warn('API override storage is unavailable', error);
    return null;
  }
}

function resolveStore(store?: OverrideStore): OverrideStore | null {
  return store ?? browserStore();
}

export function stripTrailingSlashes(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

export function normalizeSubdomain(raw: string): string {
  return raw.trim().toLowerCase().replace(/^\.+|\.+$/g, '');
}

export function validateSubdomain(raw: string): ValidSubdomain | InvalidSubdomain {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return { valid: false, error: 'empty' };
  if (trimmed.includes('://') || trimmed.includes('/') || trimmed.includes(' ')) {
    return { valid: false, error: 'scheme_or_path' };
  }
  if (trimmed.includes('.')) return { valid: false, error: 'contains_dot' };
  if (!PREVIEW_LABEL.test(trimmed)) return { valid: false, error: 'invalid_characters' };
  return { valid: true, subdomain: trimmed };
}

export function composePreviewBaseUrl(subdomain: string): string {
  const label = normalizeSubdomain(subdomain);
  return `https://${label}.${API_PREVIEW_DOMAIN}`;
}

export function extractPreviewSubdomain(url: string): string | null {
  const withoutScheme = url.trim().replace(/^https:\/\//i, '').replace(/^http:\/\//i, '');
  const host = withoutScheme.split('/')[0]?.split(':')[0] ?? '';
  const suffix = `.${API_PREVIEW_DOMAIN}`;
  if (!host.toLowerCase().endsWith(suffix)) return null;

  const subdomain = normalizeSubdomain(host.slice(0, -suffix.length));
  const validation = validateSubdomain(subdomain);
  return validation.valid ? validation.subdomain : null;
}

function defaultScheme(host: string): 'http' | 'https' {
  if (host === 'localhost' || isValidIpv4(host)) return 'http';
  return 'https';
}

function isValidIpv4(host: string): boolean {
  const match = IPV4_PATTERN.exec(host);
  if (!match) return false;
  return match.slice(1).every((part) => {
    const value = Number(part);
    return value >= 0 && value <= 255;
  });
}

function isValidPort(port: string): boolean {
  if (!/^\d+$/.test(port)) return false;
  const value = Number(port);
  return value >= 1 && value <= 65535;
}

function isHostnameLabel(label: string): boolean {
  return PREVIEW_LABEL.test(label);
}

function isAllowedFreeformHost(host: string): boolean {
  if (host === 'localhost') return true;
  if (isValidIpv4(host)) return true;
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) return false;
  return host.split('.').every(isHostnameLabel);
}

export function validateFreeformBaseUrl(raw: string): ValidFreeformUrl | InvalidFreeformUrl {
  const trimmed = raw.trim();
  if (!trimmed) return { valid: false, error: 'empty' };
  if (trimmed.includes(' ')) return { valid: false, error: 'invalid_url' };

  const existingScheme = trimmed.indexOf('://');
  const typedScheme = existingScheme > 0;
  if (typedScheme) {
    const schemeName = trimmed.slice(0, existingScheme).toLowerCase();
    if (schemeName !== 'http' && schemeName !== 'https') {
      return { valid: false, error: 'unsupported_scheme' };
    }
  }

  const withScheme = typedScheme ? trimmed : `https://${trimmed}`;
  const schemeSeparator = withScheme.indexOf('://');
  if (schemeSeparator <= 0) return { valid: false, error: 'invalid_url' };

  const scheme = withScheme.slice(0, schemeSeparator).toLowerCase();
  if (scheme !== 'http' && scheme !== 'https') {
    return { valid: false, error: 'unsupported_scheme' };
  }

  const afterScheme = withScheme.slice(schemeSeparator + 3);
  if (!afterScheme) return { valid: false, error: 'missing_host' };

  const authority = afterScheme.split('/')[0] ?? '';
  if (!authority) return { valid: false, error: 'missing_host' };

  const host = authority.split(':')[0]?.trim().toLowerCase() ?? '';
  if (!host || !isAllowedFreeformHost(host)) {
    return { valid: false, error: 'missing_host' };
  }
  if (!isEligibleDebugHost(host)) {
    return { valid: false, error: 'host_not_allowed' };
  }

  const port = authority.includes(':') ? authority.slice(authority.indexOf(':') + 1) : '';
  if (port && !isValidPort(port)) return { valid: false, error: 'invalid_url' };

  const pathAndQuery = afterScheme.includes('/') ? afterScheme.slice(afterScheme.indexOf('/') + 1) : '';
  const pathOnly = pathAndQuery.split('?')[0]?.split('#')[0] ?? '';
  if (pathOnly) return { valid: false, error: 'invalid_url' };

  const normalizedAuthority = port ? `${host}:${port}` : host;
  const resolvedScheme = typedScheme ? scheme : defaultScheme(host);
  return { valid: true, url: `${resolvedScheme}://${normalizedAuthority}` };
}

export function isAllowedApiOverrideUrl(raw: string): boolean {
  return validateFreeformBaseUrl(raw).valid;
}

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^\[|\]$/g, '');
  } catch {
    return null;
  }
}

function isPrivateOrLoopbackIpv4(hostname: string): boolean {
  if (!isValidIpv4(hostname)) return false;
  const [first, second] = hostname.split('.').map(Number);
  if (first === 127 || first === 10) return true;
  if (first === 192 && second === 168) return true;
  return first === 172 && second >= 16 && second <= 31;
}

const HEALTH_PROBE_SUFFIXES = ['oneteam.io', 'oneteam-preview.io', 'flex-appeal.nl'];

function isEligibleDebugHost(hostname: string): boolean {
  if (isBrowserProbeHost(`http://${hostname}`)) return true;
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  return HEALTH_PROBE_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
}

export function isBrowserProbeHost(baseUrl: string): boolean {
  const hostname = hostnameOf(baseUrl);
  if (!hostname) return false;
  if (hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '::1') return true;
  return isPrivateOrLoopbackIpv4(hostname);
}

function isPreviewApiHostname(hostname: string | null): boolean {
  if (!hostname) return false;
  const host = hostname.toLowerCase();
  return host === API_PREVIEW_DOMAIN || host.endsWith(`.${API_PREVIEW_DOMAIN}`);
}

export function shouldProbeHealthInBrowser(baseUrl: string): boolean {
  const normalized = stripTrailingSlashes(baseUrl);
  if (isBrowserProbeHost(normalized)) return true;
  if (typeof window === 'undefined') return false;
  return isPreviewApiHostname(hostnameOf(normalized));
}

export function isAllowedDebugDeepLinkUrl(raw: string): boolean {
  const validation = validateFreeformBaseUrl(raw);
  if (!validation.valid) return false;
  const hostname = hostnameOf(validation.url);
  if (!hostname) return false;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') return true;
  return hostname === 'oneteam-preview.io' || hostname.endsWith('.oneteam-preview.io');
}

export function composedPreviewUrl(draft: string): string {
  const normalized = normalizeSubdomain(draft);
  const validation = validateSubdomain(normalized);
  if (validation.valid) return composePreviewBaseUrl(validation.subdomain);
  return `https://${normalized || '…'}.${API_PREVIEW_DOMAIN}`;
}

export function composedFreeformUrl(draft: string): string {
  const validation = validateFreeformBaseUrl(draft);
  if (validation.valid) return validation.url;
  return draft.trim() || '…';
}

function readMode(raw: string | null): ApiOverrideMode | null {
  if (raw === 'preview_subdomain' || raw === 'freeform_url') return raw;
  return null;
}

function readSource(raw: string | null): ApiOverrideSource | null {
  if (raw === 'auto' || raw === 'manual') return raw;
  return null;
}

export function readOverride(store?: OverrideStore): StoredOverride {
  const resolved = resolveStore(store);
  if (!resolved) return EMPTY_OVERRIDE;

  const baseUrlRaw = resolved.getItem(API_BASE_URL_OVERRIDE_KEY);
  const baseUrl = baseUrlRaw ? stripTrailingSlashes(baseUrlRaw) : null;
  let mode = readMode(resolved.getItem(API_OVERRIDE_MODE_KEY));
  const source = readSource(resolved.getItem(API_OVERRIDE_SOURCE_KEY));
  let subdomain = resolved.getItem(API_OVERRIDE_SUBDOMAIN_KEY);

  if (!mode) {
    if (subdomain) {
      mode = 'preview_subdomain';
    } else if (baseUrl) {
      const extracted = extractPreviewSubdomain(baseUrl);
      if (extracted) {
        mode = 'preview_subdomain';
        subdomain = extracted;
      } else {
        mode = 'freeform_url';
      }
    }
  }

  if (mode === 'preview_subdomain' && !subdomain && baseUrl) {
    subdomain = extractPreviewSubdomain(baseUrl);
  }

  return {
    mode,
    source,
    subdomain: subdomain ? normalizeSubdomain(subdomain) : null,
    baseUrl,
  };
}

export function writePreviewOverride(
  subdomain: string,
  source: ApiOverrideSource,
  store?: OverrideStore,
): void {
  const resolved = resolveStore(store);
  if (!resolved) return;

  const normalized = normalizeSubdomain(subdomain);
  resolved.setItem(API_BASE_URL_OVERRIDE_KEY, composePreviewBaseUrl(normalized));
  resolved.setItem(API_OVERRIDE_MODE_KEY, 'preview_subdomain');
  resolved.setItem(API_OVERRIDE_SOURCE_KEY, source);
  resolved.setItem(API_OVERRIDE_SUBDOMAIN_KEY, normalized);
}

export function writeFreeformOverride(
  baseUrl: string,
  source: ApiOverrideSource,
  store?: OverrideStore,
): void {
  const resolved = resolveStore(store);
  if (!resolved) return;

  resolved.setItem(API_BASE_URL_OVERRIDE_KEY, stripTrailingSlashes(baseUrl));
  resolved.setItem(API_OVERRIDE_MODE_KEY, 'freeform_url');
  resolved.setItem(API_OVERRIDE_SOURCE_KEY, source);
  resolved.removeItem(API_OVERRIDE_SUBDOMAIN_KEY);
}

export function clearOverride(store?: OverrideStore): void {
  const resolved = resolveStore(store);
  if (!resolved) return;

  resolved.removeItem(API_BASE_URL_OVERRIDE_KEY);
  resolved.removeItem(API_OVERRIDE_MODE_KEY);
  resolved.removeItem(API_OVERRIDE_SOURCE_KEY);
  resolved.removeItem(API_OVERRIDE_SUBDOMAIN_KEY);
}

export function persistManualOverrideFromRawUrl(raw: string, store?: OverrideStore): boolean {
  const validation = validateFreeformBaseUrl(raw);
  if (!validation.valid) return false;

  const subdomain = extractPreviewSubdomain(validation.url);
  if (subdomain) {
    writePreviewOverride(subdomain, 'manual', store);
  } else {
    writeFreeformOverride(validation.url, 'manual', store);
  }
  return true;
}

export function shouldApply(stored: StoredOverride, targetSubdomain: string): boolean {
  if (stored.mode === 'freeform_url' && stored.baseUrl) return false;

  const current = stored.mode === 'preview_subdomain' ? stored.subdomain : null;
  if (!current) return true;
  if (current === normalizeSubdomain(targetSubdomain)) return false;
  return stored.source === 'auto';
}

export function canSavePreviewDraft(
  draft: string,
  stored: StoredOverride,
  defaultBaseUrl: string,
): boolean {
  const normalized = normalizeSubdomain(draft);
  if (!validateSubdomain(normalized).valid) return false;
  if (stored.mode === 'freeform_url' && stored.baseUrl) return true;

  const active = stored.mode === 'preview_subdomain' ? stored.subdomain : null;
  if (active) return normalized !== active;

  const defaultSubdomain = extractPreviewSubdomain(defaultBaseUrl);
  if (!defaultSubdomain) return true;
  return normalized !== defaultSubdomain;
}

export function canSaveFreeformDraft(
  draft: string,
  stored: StoredOverride,
  defaultBaseUrl: string,
): boolean {
  const validation = validateFreeformBaseUrl(draft);
  if (!validation.valid) return false;

  if (stored.mode === 'freeform_url' && stored.baseUrl) {
    return validation.url !== stripTrailingSlashes(stored.baseUrl);
  }
  if (stored.baseUrl) return validation.url !== stripTrailingSlashes(stored.baseUrl);
  return validation.url !== stripTrailingSlashes(defaultBaseUrl);
}

export function canClearOverride(stored: StoredOverride): boolean {
  return !!stored.baseUrl;
}

export function previewSubdomainFromHost(hostOrUrl: string): string | null {
  const trimmed = hostOrUrl.trim();
  let hostname = trimmed;
  if (trimmed.includes('://')) {
    try {
      hostname = new URL(trimmed).hostname;
    } catch {
      return null;
    }
  }

  const match = WEB_PREVIEW_HOST.exec(hostname);
  const pullRequestId = match?.[1];
  if (!pullRequestId) return null;
  return `pr-${pullRequestId}-api`;
}

export function filterPreviewEnvironments(
  environments: PreviewEnvironment[],
  query: string,
): PreviewEnvironment[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return environments;

  return environments.filter((environment) => {
    const haystack = [
      environment.label,
      environment.url,
      environment.kind,
      environment.subdomain,
      environment.pullRequestId != null ? String(environment.pullRequestId) : '',
      environment.pullRequestId != null ? `#${environment.pullRequestId}` : '',
      environment.commit ?? '',
      environment.commitMessage ?? '',
    ].join(' ').toLowerCase();
    return haystack.includes(needle);
  });
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function readOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return undefined;
}

export function parsePreviewEnvironments(body: unknown): PreviewEnvironment[] {
  const data = body && typeof body === 'object' && 'data' in body
    ? (body as { data?: unknown }).data
    : null;
  if (!Array.isArray(data)) return [];

  return data.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const url = typeof record.url === 'string' ? record.url : '';
    const subdomain = extractPreviewSubdomain(url);
    if (!subdomain) return [];

    return [{
      label: typeof record.label === 'string' && record.label.trim() ? record.label : subdomain,
      url,
      kind: typeof record.kind === 'string' ? record.kind : '',
      subdomain,
      pullRequestId: readOptionalNumber(record.pull_request_id),
      commit: readOptionalString(record.commit),
      commitMessage: readOptionalString(record.commit_message),
      live: typeof record.live === 'boolean' ? record.live : undefined,
    }];
  });
}

export function catalogErrorFromStatus(status: number, serverMessage?: string): CatalogErrorState {
  if (status === 401 || status === 403) return { error: 'unauthorized', httpCode: status, serverMessage };
  return { error: 'http', httpCode: status, serverMessage };
}

export function catalogErrorFromThrown(error: unknown): CatalogErrorState {
  if (error instanceof TypeError) return { error: 'no_internet' };
  return { error: 'unknown' };
}

export function environmentDetailLabel(environment: PreviewEnvironment): string | null {
  const parts: string[] = [];
  if (environment.pullRequestId != null) parts.push(`#${environment.pullRequestId}`);
  if (environment.commit) parts.push(environment.commit);
  if (environment.commitMessage) parts.push(environment.commitMessage);
  if (environment.live) parts.push('Live');
  return parts.length ? parts.join(' · ') : null;
}

export function environmentSublabel(environment: PreviewEnvironment): string {
  const parts = [environment.kind, environment.url];
  const detail = environmentDetailLabel(environment);
  if (detail) parts.push(detail);
  return parts.filter(Boolean).join(' · ');
}

export function hostLabel(url: string): string {
  return stripTrailingSlashes(url).replace(/^https:\/\//i, '').replace(/^http:\/\//i, '');
}

const PR_PREVIEW_SUBDOMAIN = /^pr-(\d+)-api$/i;

export function previewButtonLabel(baseUrl: string): string | null {
  const subdomain = extractPreviewSubdomain(baseUrl);
  if (!subdomain) return null;
  const pullRequest = PR_PREVIEW_SUBDOMAIN.exec(subdomain);
  if (pullRequest?.[1]) return `pr-${pullRequest[1]}`;
  return subdomain;
}

export function environmentDisplayName(
  stored: StoredOverride,
  environments: PreviewEnvironment[],
): string {
  if (!stored.baseUrl) return DEFAULT_ENVIRONMENT_NAME;

  const previewLabel = previewButtonLabel(stored.baseUrl);
  if (previewLabel) return previewLabel;

  if (stored.mode === 'freeform_url') return hostLabel(stored.baseUrl);

  const subdomain = stored.subdomain ?? extractPreviewSubdomain(stored.baseUrl);
  if (!subdomain) return hostLabel(stored.baseUrl);

  const match = environments.find((environment) => environment.subdomain === subdomain);
  return match?.label || subdomain;
}

export function readCachedEnvironmentName(effectiveUrl: string, store?: OverrideStore): string | null {
  const resolved = resolveStore(store);
  if (!resolved) return null;

  const cachedUrl = resolved.getItem(API_ENV_DISPLAY_URL_KEY);
  const cachedName = resolved.getItem(API_ENV_DISPLAY_NAME_KEY);
  if (!cachedUrl || !cachedName || stripTrailingSlashes(cachedUrl) !== stripTrailingSlashes(effectiveUrl)) {
    return null;
  }
  return cachedName;
}

export function writeCachedEnvironmentName(
  effectiveUrl: string,
  name: string,
  store?: OverrideStore,
): void {
  const resolved = resolveStore(store);
  if (!resolved || !name.trim()) return;
  resolved.setItem(API_ENV_DISPLAY_URL_KEY, stripTrailingSlashes(effectiveUrl));
  resolved.setItem(API_ENV_DISPLAY_NAME_KEY, name);
}

export function shouldOfferApiRecovery(error: unknown): boolean {
  return error instanceof TypeError;
}

const networkFailureListeners = new Set<() => void>();

export function subscribeApiNetworkFailure(listener: () => void): () => void {
  networkFailureListeners.add(listener);
  return () => {
    networkFailureListeners.delete(listener);
  };
}

export function notifyApiNetworkFailure(): void {
  if (!process.env.API_ENV || process.env.API_ENV === 'production') return;
  networkFailureListeners.forEach((listener) => listener());
}

async function verifyApiHostInBrowser(baseUrl: string, signal?: AbortSignal): Promise<HealthResult> {
  const normalized = stripTrailingSlashes(baseUrl);
  const response = await fetch(`${normalized}/health`, {
    headers: { Accept: 'application/json' },
    credentials: isPreviewApiHostname(hostnameOf(normalized)) ? 'include' : 'same-origin',
    signal,
  });
  if (!response.ok) return 'unhealthy';
  const body = await response.json() as { status?: unknown };
  if (typeof body?.status === 'string' && body.status.toLowerCase() === 'ok') return 'ok';
  return 'unhealthy';
}

async function verifyApiHostOnServer(baseUrl: string, signal?: AbortSignal): Promise<HealthResult> {
  const probe = `/api/debug-health?url=${encodeURIComponent(stripTrailingSlashes(baseUrl))}`;
  const response = await fetch(probe, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) return 'unreachable';

  let body: { status?: unknown } | null = null;
  try {
    body = await response.json() as { status?: unknown };
  } catch {
    return 'unreachable';
  }
  if (body?.status === 'ok') return 'ok';
  if (body?.status === 'unhealthy') return 'unhealthy';
  return 'unreachable';
}

export async function verifyApiHost(baseUrl: string, signal?: AbortSignal): Promise<HealthResult> {
  try {
    const normalized = stripTrailingSlashes(baseUrl);
    if (shouldProbeHealthInBrowser(normalized)) return await verifyApiHostInBrowser(normalized, signal);
    return await verifyApiHostOnServer(normalized, signal);
  } catch {
    return 'unreachable';
  }
}

export async function runAutoSelect(options: RunAutoSelectOptions): Promise<'applied' | 'skipped'> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? AUTO_SELECT_TIMEOUT_MS);
  const abortFromParent = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromParent);

  try {
    const subdomain = previewSubdomainFromHost(options.hostname);
    if (!subdomain || controller.signal.aborted) return 'skipped';

    const stored = (options.read ?? readOverride)();
    if (!shouldApply(stored, subdomain)) return 'skipped';

    const health = await options.verify(composePreviewBaseUrl(subdomain), controller.signal);
    if (health !== 'ok' || controller.signal.aborted) return 'skipped';

    const write = options.write ?? ((next: string) => writePreviewOverride(next, 'auto'));
    write(subdomain);
    return 'applied';
  } catch {
    return 'skipped';
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener('abort', abortFromParent);
  }
}
